import { useState } from "react";
import { mutate } from "swr";
import { Loader2 } from "lucide-react";

import { OrganizationService, type OrgMember } from "@/services/organization.service";
import { FEATURES, FEATURE_LABELS, type FeatureKey } from "@/constants/permissions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

// Mirror of the backend role defaults (permissions.constants.ts) so the
// toggles show what a member gets when no override is set.
const MEMBER_ROLE_DEFAULTS: Record<FeatureKey, boolean> = {
  catalog: true,
  ai_tools: true,
  analytics: true,
  api_keys: false,
  billing: false,
  support: true,
  team: false,
};

interface MemberPermissionsDialogProps {
  member: OrgMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MemberPermissionsDialog({
  member,
  open,
  onOpenChange,
}: MemberPermissionsDialogProps) {
  const [overrides, setOverrides] = useState<Partial<Record<FeatureKey, boolean>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed local state each time the dialog opens for a member.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (member && open && seededFor !== member.id) {
    setOverrides(member.permissions ?? {});
    setSeededFor(member.id);
    setError(null);
  }
  if (!open && seededFor !== null) {
    setSeededFor(null);
  }

  const effective = (feature: FeatureKey): boolean =>
    overrides[feature] ?? MEMBER_ROLE_DEFAULTS[feature];

  const isOverridden = (feature: FeatureKey): boolean =>
    overrides[feature] !== undefined && overrides[feature] !== MEMBER_ROLE_DEFAULTS[feature];

  const toggle = (feature: FeatureKey, value: boolean) => {
    setOverrides((current) => {
      const next = { ...current };
      if (value === MEMBER_ROLE_DEFAULTS[feature]) {
        delete next[feature];
      } else {
        next[feature] = value;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!member) return;
    setIsSaving(true);
    setError(null);
    try {
      await OrganizationService.updateMemberPermissions(member.id, overrides);
      mutate("organization-members");
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message || "Failed to update permissions. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Feature permissions</DialogTitle>
          <DialogDescription>
            {member
              ? `Control which features ${member.user.name || member.user.email} can access. Toggles marked "custom" differ from the member-role default.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {FEATURES.map((feature) => (
            <div
              key={feature}
              className="flex items-center justify-between gap-4 rounded-lg border p-3"
            >
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{FEATURE_LABELS[feature].label}</p>
                  {isOverridden(feature) && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                      custom
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {FEATURE_LABELS[feature].description}
                </p>
              </div>
              <Switch
                checked={effective(feature)}
                onCheckedChange={(value) => toggle(feature, value)}
                aria-label={`Toggle ${FEATURE_LABELS[feature].label}`}
              />
            </div>
          ))}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => setOverrides({})} disabled={isSaving}>
            Reset to role defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !member}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
